import { useState, useEffect } from "react";

/**
 * Custom hook to track screen size and responsive breakpoints
 * @returns {Object} Screen size information and breakpoint booleans
 */
export const useScreenSize = () => {
  // Define breakpoints (in pixels)
  const BREAKPOINTS = {
    mobile: 768, // Below 768px
    tablet: 1024, // 768px to 1024px
    desktop: 1200, // Above 1024px (widescreen starts at 1200px)
  };

  // Initialize state with current window dimensions
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  // Calculate breakpoint booleans
  const isMobile = screenSize.width < BREAKPOINTS.mobile;
  const isTablet =
    screenSize.width >= BREAKPOINTS.mobile &&
    screenSize.width < BREAKPOINTS.tablet;
  const isDesktop = screenSize.width >= BREAKPOINTS.tablet;
  const isWideScreen = screenSize.width >= BREAKPOINTS.desktop;

  useEffect(() => {
    // Handle resize event
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial size on mount
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return {
    // Screen dimensions
    width: screenSize.width,
    height: screenSize.height,

    // Breakpoint booleans
    isMobile,
    isTablet,
    isDesktop,
    isWideScreen,

    // Additional utility
    breakpoint: isMobile
      ? "mobile"
      : isTablet
      ? "tablet"
      : isWideScreen
      ? "widescreen"
      : "desktop",

    // Breakpoint values for reference
    breakpoints: BREAKPOINTS,
  };
};
